// Feature 12: Evidence Chain Component

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Stack,
} from '@mui/material';
import {
  ExpandMore,
  Assessment,
  Schedule,
  Science,
  AutoGraph,
} from '@mui/icons-material';
import { EvidenceItem } from '../../types/insightExplainability.types';

interface EvidenceChainProps {
  evidence: EvidenceItem[];
  expandAll?: boolean;
}

const EvidenceChain: React.FC<EvidenceChainProps> = ({ evidence, expandAll = false }) => {
  const [expanded, setExpanded] = useState<string[]>(expandAll ? evidence.map((e) => e.id) : []);

  const handleAccordionChange = (evidenceId: string) => {
    setExpanded((prev) =>
      prev.includes(evidenceId) ? prev.filter((id) => id !== evidenceId) : [...prev, evidenceId]
    );
  };

  const getEvidenceIcon = (type: string) => {
    switch (type) {
      case 'STATISTICAL':
        return <Assessment color="primary" />;
      case 'TEMPORAL':
        return <Schedule color="secondary" />;
      case 'DOMAIN_KNOWLEDGE':
        return <Science color="info" />;
      case 'EXPERIMENTAL':
        return <AutoGraph color="success" />;
      default:
        return <Assessment />;
    }
  };

  const getEvidenceColor = (confidence: number) => {
    if (confidence >= 0.8) return 'success';
    if (confidence >= 0.6) return 'info';
    if (confidence >= 0.4) return 'warning';
    return 'error';
  };

  if (!evidence || evidence.length === 0) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Typography color="text.secondary">No evidence available for this insight.</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Evidence Chain ({evidence.length} items)
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
        Supporting data that backs this insight
      </Typography>

      <Stack spacing={2}>
        {evidence.map((item, index) => (
          <Accordion
            key={item.id}
            expanded={expanded.includes(item.id)}
            onChange={() => handleAccordionChange(item.id)}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box display="flex" alignItems="center" gap={2} width="100%">
                {getEvidenceIcon(item.evidenceType)}
                <Box flex={1}>
                  <Typography variant="subtitle2">
                    {index + 1}. {item.description}
                  </Typography>
                  <Box display="flex" gap={1} mt={0.5}>
                    <Chip label={item.source} size="small" variant="outlined" />
                    <Chip
                      label={`${Math.round(item.confidenceScore * 100)}% confidence`}
                      size="small"
                      color={getEvidenceColor(item.confidenceScore)}
                    />
                  </Box>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box>
                {/* Confidence Bar */}
                <Box mb={2}>
                  <Typography variant="caption" color="text.secondary">
                    Confidence Score
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={item.confidenceScore * 100}
                    color={getEvidenceColor(item.confidenceScore)}
                    sx={{ height: 8, borderRadius: 1, mt: 0.5 }}
                  />
                </Box>

                {/* Supporting Data Table */}
                {item.supportingData && item.supportingData.length > 0 && (
                  <Box mb={2}>
                    <Typography variant="subtitle2" gutterBottom>
                      Supporting Metrics
                    </Typography>
                    <Table size="small">
                      <TableBody>
                        {item.supportingData.map((data, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{data.metric}</TableCell>
                            <TableCell align="right">
                              <strong>{data.value.toLocaleString()}</strong>
                            </TableCell>
                            {data.baseline !== undefined && (
                              <TableCell align="right" sx={{ color: 'text.secondary' }}>
                                Baseline: {data.baseline.toLocaleString()}
                              </TableCell>
                            )}
                            {data.changePercent !== undefined && (
                              <TableCell
                                align="right"
                                sx={{
                                  color: data.changePercent > 0 ? 'success.main' : 'error.main',
                                }}
                              >
                                {data.changePercent > 0 ? '+' : ''}
                                {data.changePercent.toFixed(1)}%
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}

                {/* Date Range */}
                <Typography variant="caption" color="text.secondary">
                  Period: {new Date(item.dateRange.start).toLocaleDateString()} to{' '}
                  {new Date(item.dateRange.end).toLocaleDateString()}
                </Typography>
              </Box>
            </AccordionDetails>
          </Accordion>
        ))}
      </Stack>
    </Box>
  );
};

export default EvidenceChain;
